// Mappers for Onboarding 2.0 - GQL → Domain types
// Transforms GraphQL response types to domain types

import {
  GetOnboardingQuestionsQuery,
  GetOnboardingResponsesQuery,
  GetOnboardingDismissalQuery,
  GetStrategiesQuery,
  GetStrategyScoringCategoriesQuery,
  GetStrategyScoringRulesQuery,
  OnboardingQuestionType as GQLOnboardingQuestionType,
  BookingClassificationStatus as GQLBookingClassificationStatus,
} from 'generated/graphql';
import {
  OnboardingQuestion,
  OnboardingAnswer,
  OnboardingResponse,
  OnboardingDismissal,
  Strategy,
  CaseStudy,
  ScoringCategory,
  ScoringRule,
  OnboardingQuestionType,
  BookingEligibility,
} from './Onboarding.types';

// Helper type for array items
type ArrayItem<T> = T extends Array<infer U> ? U : never;

// GQL types aliases for convenience
export type GQLQuestion = ArrayItem<
  GetOnboardingQuestionsQuery['GetOnboardingQuestions']
>;
export type GQLAnswer = ArrayItem<GQLQuestion['answers']>;
export type GQLResponse = ArrayItem<
  GetOnboardingResponsesQuery['GetOnboardingResponses']
>;
export type GQLDismissal =
  GetOnboardingDismissalQuery['GetOnboardingDismissal'];
export type GQLStrategy = ArrayItem<GetStrategiesQuery['GetStrategies']>;
export type GQLCaseStudy = ArrayItem<GQLStrategy['caseStudies']>;
export type GQLScoringCategory = ArrayItem<
  GetStrategyScoringCategoriesQuery['GetStrategyScoringCategory']
>;
export type GQLScoringRule = ArrayItem<
  GetStrategyScoringRulesQuery['GetStrategyScoringRules']
>;

// Map GQL question type enum to domain type.
// Compares the underlying string value rather than referencing every enum
// member, so the mapping remains stable across codegen regenerations.
export const mapQuestionType = (
  type: GQLOnboardingQuestionType,
): OnboardingQuestionType => {
  switch (type as string) {
    case 'SINGLE_CHOICE':
      return 'SINGLE_CHOICE';
    case 'MULTI_CHOICE':
      return 'MULTI_CHOICE';
    case 'OPEN_TEXT':
      return 'OPEN_TEXT';
    default:
      return 'SINGLE_CHOICE';
  }
};

// Map single answer
export const mapAnswer = (
  answer: GQLAnswer,
  chartValue: number,
): OnboardingAnswer => ({
  id: answer.id,
  title: answer.title,
  subtitle: answer.subtitle ?? null,
  weight: answer.weight ?? 1, // Default to 1 if not provided
  chartValue,
});

const getNormalizedChartValues = (count: number): number[] => {
  if (count <= 0) return [];
  if (count === 1) return [4];
  if (count === 2) return [1, 4];
  if (count === 3) return [1, 2.5, 4];
  if (count === 4) return [1, 2, 3, 4];

  return [1, 2, 3, 4, ...Array.from({ length: count - 4 }, () => 4)];
};

/**
 * Normalizes answer weights to the 1-4 chart scale.
 *
 * Rules:
 * - 1 answer: [4]
 * - 2 answers: [1, 4]
 * - 3 answers: [1, 2.5, 4]
 * - 4 answers: [1, 2, 3, 4]
 * - >4 answers: map first 4 by weight to [1,2,3,4], clamp remaining to 4
 * - Missing weight: weight = 1, chartValue = 1
 * - No category: all chartValues = 1
 *
 * @param answers - Raw GQL answers with backend weights
 * @param hasCategory - Whether the question has a target category
 * @returns Normalized answers with chartValue in the 1-4 range
 */
export const normalizeAnswerChartValues = (
  answers: GQLAnswer[],
  hasCategory: boolean,
): OnboardingAnswer[] => {
  if (!hasCategory) {
    return [...answers]
      .sort((a, b) => (a.weight ?? Infinity) - (b.weight ?? Infinity))
      .map((answer) => mapAnswer(answer, 1));
  }

  const answersWithWeight = answers.filter(
    (answer) => answer.weight !== null && answer.weight !== undefined,
  );
  const sortedByWeight = [...answersWithWeight].sort((a, b) => {
    const weightA = a.weight ?? 0;
    const weightB = b.weight ?? 0;
    return weightA - weightB;
  });
  const normalizedValues = getNormalizedChartValues(sortedByWeight.length);
  const chartValueById = new Map<string, number>();

  sortedByWeight.forEach((answer, index) => {
    const chartValue = normalizedValues[index] ?? 1;
    chartValueById.set(answer.id, chartValue);
  });

  return [...answers]
    .sort((a, b) => (a.weight ?? Infinity) - (b.weight ?? Infinity))
    .map((answer) => {
      const isMissingWeight =
        answer.weight === null || answer.weight === undefined;
      const chartValue = isMissingWeight
        ? 1
        : chartValueById.get(answer.id) ?? 1;
      return mapAnswer(answer, chartValue);
    });
};

// Map single question
export const mapQuestion = (question: GQLQuestion): OnboardingQuestion => {
  const hasCategory = question.targetCategory !== null;

  return {
    id: question.id,
    title: question.title,
    subtitle: question.subtitle ?? null,
    type: mapQuestionType(question.type),
    targetCategory: question.targetCategory,
    isRequired: question.isRequired,
    answers: normalizeAnswerChartValues(question.answers, hasCategory),
  };
};

// Map array of questions
export const mapQuestions = (
  questions: GetOnboardingQuestionsQuery['GetOnboardingQuestions'],
): OnboardingQuestion[] => questions.map(mapQuestion);

// Map single response. The query selects the canonical `answerIds` field,
// which the server always populates (empty array for OPEN_TEXT).
export const mapResponse = (response: GQLResponse): OnboardingResponse => ({
  questionId: response.questionId,
  // Dedup so downstream consumers (chips, chart values) never double-count
  // a repeated id from the server.
  answerIds: Array.from(new Set(response.answerIds)),
  textValue: response.textValue ?? null,
});

// Map array of responses
export const mapResponses = (
  responses: GetOnboardingResponsesQuery['GetOnboardingResponses'],
): OnboardingResponse[] => responses.map(mapResponse);

// Map dismissal state
export const mapDismissal = (dismissal: GQLDismissal): OnboardingDismissal => ({
  webDismissedAt: dismissal.webDismissedAt ?? null,
  mobileDismissedAt: dismissal.mobileDismissedAt ?? null,
});

export const toOnboardingDismissal = (
  dismissal: GQLDismissal | null | undefined,
): OnboardingDismissal => {
  if (!dismissal) {
    return {
      webDismissedAt: null,
      mobileDismissedAt: null,
    };
  }

  return mapDismissal(dismissal);
};

export const isDismissedOnAnyPlatform = (
  dismissal: OnboardingDismissal,
): boolean => {
  return Boolean(dismissal.webDismissedAt || dismissal.mobileDismissedAt);
};

// Map single case study
export const mapCaseStudy = (caseStudy: GQLCaseStudy): CaseStudy => ({
  id: caseStudy.id,
  strategyId: caseStudy.strategyId,
  title: caseStudy.title,
  subtitle: caseStudy.subtitle ?? null,
  description: caseStudy.description ?? null,
  coverImageHorizontalUrl: caseStudy.coverImageHorizontalUrl,
  coverImageVerticalUrl: caseStudy.coverImageVerticalUrl,
  creatorName: caseStudy.creatorName,
  creatorInstagramName: caseStudy.creatorInstagramName,
  documentUrl: caseStudy.documentUrl,
});

// Map single strategy
export const mapStrategy = (strategy: GQLStrategy): Strategy => ({
  id: strategy.id,
  name: strategy.name,
  tagline: strategy.tagline ?? null,
  description: strategy.description ?? null,
  superTitle: strategy.superTitle ?? null,
  pills: strategy.pills,
  features: strategy.features,
  priceLabel: strategy.priceLabel ?? null,
  programLengthLabel: strategy.programLengthLabel ?? null,
  launchTimeLabel: strategy.launchTimeLabel ?? null,
  coverImageUrl: strategy.coverImageUrl ?? null,
  isRecommended: strategy.isRecommended,
  isActive: strategy.isActive,
  isSelected: strategy.isSelected ?? false,
  caseStudies: strategy.caseStudies.map(mapCaseStudy),
});

// Map array of strategies (filter only active)
export const mapStrategies = (
  strategies: GetStrategiesQuery['GetStrategies'],
): Strategy[] => strategies.filter((s) => s.isActive).map(mapStrategy);

// Map scoring category
export const mapScoringCategory = (
  category: GQLScoringCategory,
): ScoringCategory => ({
  id: category.id,
  name: category.name,
});

// Map array of scoring categories
export const mapScoringCategories = (
  categories: GetStrategyScoringCategoriesQuery['GetStrategyScoringCategory'],
): ScoringCategory[] => categories.map(mapScoringCategory);

// Map scoring rule
export const mapScoringRule = (rule: GQLScoringRule): ScoringRule => ({
  id: rule.id,
  strategyId: rule.strategyId,
  categoryId: rule.categoryId,
  minScore: rule.minScore ?? 0,
  maxScore: rule.maxScore ?? 100,
});

// Map array of scoring rules
export const mapScoringRules = (
  rules: GetStrategyScoringRulesQuery['GetStrategyScoringRules'],
): ScoringRule[] => rules.map(mapScoringRule);

// Map the booking classification status to the domain eligibility.
// Compares the underlying string value (same rationale as mapQuestionType).
// `Incomplete` cannot occur at the strategy-selection call site — answers
// were just saved — so it falls through to 'not-eligible' (route to the
// guide), preserving the pre-status behavior.
export const toBookingEligibility = (
  status: GQLBookingClassificationStatus | undefined,
): BookingEligibility => {
  switch (status as string | undefined) {
    case 'Eligible':
      return 'eligible';
    case 'Pending':
      return 'pending';
    default:
      return 'not-eligible';
  }
};
